
import React, { Fragment, useEffect, useState } from 'react'
import { trans } from '../../../_providers/Translation'
import { Table, TableBody, TableCell, TableContainer, TableHead, Paper, TextField, Select, MenuItem, InputAdornment, Avatar, LinearProgress, Typography, Box, IconButton, Tooltip } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import NoDataFound from '../../partials/NoDataFound'
import Options from './partials/Options'
import Api from '../../../_helpers/Api'
import DateRange from '@/components/partials/DateRange'
import Skeleton from 'react-loading-skeleton'
import { useHistory, useLocation } from 'react-router'


function BonusPoliciesDashboard({
    handleEdit,
    handleDelete,
    handleShowPeriods,
    update
}) {
    const [dateRange, setDateRange] = React.useState({ start: null, end: null })
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [periods, setPeriods] = useState([])
    const [periodsLoading, setPeriodsLoading] = useState(true)
    const [selectedPeriod, setSelectedPeriod] = useState(null)
    const [periodResults, setPeriodResults] = useState(null)
    const [typeFilter, setTypeFilter] = useState('')
    const [completedTargetFilter, setCompletedTargetFilter] = useState('')

    const history = useHistory()
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    const selected = searchParams.get('id') ? Number(searchParams.get('id')) : null


    // Fetch all periods when filters change
    useEffect(() => {
        setPeriodsLoading(true)
        // Build params from filters
        const params = {}
        if (typeFilter) params.type = typeFilter
        if (completedTargetFilter) params.completed_target = completedTargetFilter
        if (dateRange.start) params.from = dateRange.start
        if (dateRange.end) params.to = dateRange.end

        // Update URL params
        const url = new URL(window.location.href)
        Object.keys(params).forEach(key => {
            if (params[key]) {
                url.searchParams.set(key, params[key])
            } else {
                url.searchParams.delete(key)
            }
        })
        window.history.replaceState({}, '', url)

        Api.get('partners/bonuspolicies/all-periods', { params })
            .then(res => {
                // Defensive: ensure array for all possible API shapes
                let periodsArr = []
                if (Array.isArray(res.data)) {
                    periodsArr = res.data
                } else if (res.data && Array.isArray(res.data.periods)) {
                    periodsArr = res.data.periods
                } else if (res.data && Array.isArray(res.data.items)) {
                    periodsArr = res.data.items
                }
                setPeriods(periodsArr)
            })
            .finally(() => setPeriodsLoading(false))
    }, [typeFilter, completedTargetFilter, dateRange.start, dateRange.end, update])

    // Fetch employees progress for selected period
    useEffect(() => {
        if (!selectedPeriod) return
        setLoading(true)
        setPeriodResults(null)
        Api.get('partners/bonuspolicies/prediction', {
            params: {
                period_id: selectedPeriod.id,
                policy_id: selectedPeriod.policy_id
            }
        })
            .then(res => {
                // For type_id=1, sometimes technicians are directly in res.data.technicians
                let allTechs = []
                if (res.data.active_policies && Array.isArray(res.data.active_policies)) {
                    allTechs = res.data.active_policies.flatMap(policy =>
                        (policy.technicians || []).map(tech => ({
                            ...tech,
                            policy_id: policy.policy_id,
                            policy_type: policy.policy_type,
                            policy_from: policy.from,
                            policy_to: policy.to
                        }))
                    )
                }
                // If no active_policies or empty, fallback to res.data.technicians
                if ((!allTechs || allTechs.length === 0) && Array.isArray(res.data.technicians)) {
                    allTechs = res.data.technicians
                }
                setRows(allTechs)
                // Save results if present
                if (res.data && res.data.results) {
                    setPeriodResults(res.data.results)
                } else {
                    setPeriodResults(null)
                }
            })
            .finally(() => setLoading(false))
    }, [selectedPeriod])

    const handleStartDateChange = date => setDateRange(prev => ({ ...prev, start: date }))
    const handleEndDateChange = date => setDateRange(prev => ({ ...prev, end: date }))

    // Handler to clear all filters
    const handleClearFilters = () => {
        setTypeFilter('')
        setCompletedTargetFilter('')
        setDateRange({ start: null, end: null })
    }

    const handleToggleRow = rowId => {

        document.querySelectorAll('.collapsable-table tr.active').forEach(el => {
            el.classList.remove('active')
        })

        let rows = document.querySelectorAll(`tr[data-row="${rowId}"]`)

        for (let i = 0; i < rows.length; i++) {
            if (rows[i].classList.contains('active')) {
                rows[i].classList.remove('active')
            } else {
                rows[i].classList.add('active')
            }
        }

        searchParams.set('id', rowId)

        history.replace('?' + searchParams.toString())

    }

    return (
        <>
            {/* <h3>{trans('tabs.dashboard')}</h3> */}
            <div className='filter-standard'>
                <div className='col'>
                    <select
                        value={typeFilter}
                        onChange={e => setTypeFilter(e.target.value)}
                    >
                        <option value="">{trans('labels.type') || 'Тип'}</option>
                        <option value={1}>{trans('labels.policyByPoints') || 'По точки'}</option>
                        <option value={2}>{trans('labels.policyByReservations') || 'По резервации'}</option>
                    </select>
                </div>
                <div className='col'>
                    <DateRange
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                        onStartDateChange={(date) => {
                            handleStartDateChange(date)
                        }} onEndDateChange={(date) => {
                            handleEndDateChange(date)
                        }}
                    />
                </div>
                <div className='col'>
                    <button className='button-clear-filter' onClick={handleClearFilters}>
                        Изчистване
                    </button>
                </div>
            </div>
            <hr />

            {periodsLoading ? (
                <Skeleton height={45} count={5} />
            ) : periods.length === 0 ? (
                <NoDataFound />
            ) : (
                <table className="collapsable-table">
                    <thead>
                        <tr>
                            <th>{trans('labels.type')}</th>
                            <th>{trans('labels.from')}</th>
                            <th>{trans('labels.to')}</th>
                            <th className="options">{trans('labels.options')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {periods.map(period => (
                            <Fragment key={'partner-' + period.id}>
                                <tr
                                    className={period.id === selected ? 'active' : ''}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        handleToggleRow(period.id)

                                        if (selectedPeriod && selectedPeriod.id === period.id) {
                                            setSelectedPeriod(null)
                                        } else {
                                            setSelectedPeriod(period)
                                        }
                                    }}
                                >
                                    {console.log(period)}
                                    <td>{period.policy.type_id === 1 ? trans('labels.policyByPoints') : trans('labels.policyByReservations')}</td>
                                    <td>{period.from_dmy}</td>
                                    <td>{period.to_dmy}</td>
                                    <td className="options">
                                        <Options
                                            id={period.policy_id}
                                            handleEdit={handleEdit}
                                            handleDelete={handleDelete}
                                            handleShowResult={handleShowPeriods}
                                        />
                                    </td>
                                </tr>
                                <tr
                                    className={['collapsable', 'unclickable', period.id === selected ? 'active' : ''].join(' ')}
                                    data-row={period.id}
                                >
                                    {selectedPeriod &&
                                        <td colSpan="4">
                                            {((selectedPeriod.policy && selectedPeriod.policy.type_id === 1) || selectedPeriod.type_id === 1) ? (
                                                <table className='unclickable'>
                                                    <thead>
                                                        <tr>
                                                            <th>{trans('labels.user')}</th>
                                                            <th>{trans('labels.reservations')}</th>
                                                            <th>{trans('labels.pointsFromRules') || 'Points (Rules)'}</th>
                                                            <th>{trans('labels.pointsFromCustomers') || 'Points (Customers)'}</th>
                                                            <th>{trans('labels.delays')}</th>
                                                            <th>{trans('labels.reclamations')}</th>
                                                            <th>{trans('labels.progress')}</th>
                                                            <th>{trans('labels.completionForecast')}</th>
                                                            <th>{trans('labels.bonus')}</th>
                                                            <th>{trans('labels.super_bonus')}</th>
                                                            <th>{trans('labels.total_earned_bonuses')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {loading ? (
                                                            <tr>
                                                                <td colSpan="11">
                                                                    <Skeleton height={45} count={5} />
                                                                </td>
                                                            </tr>
                                                        ) : rows.length === 0 ? (
                                                            <tr>
                                                                <td colSpan="11">
                                                                    <NoDataFound />
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            rows.map((row, idx) => (
                                                                <tr
                                                                    key={row.employee_id || idx}
                                                                    style={row.completed_target ? { backgroundColor: '#e6f9ed' } : {}}
                                                                >
                                                                    <td>
                                                                        <Box display="flex" alignItems="center" gap={1}>
                                                                            <Avatar style={{ width: 28, height: 28, marginRight: 8 }} />
                                                                            <span>{row.employee_name}</span>
                                                                        </Box>
                                                                    </td>
                                                                    <td>{row.total_reservations}</td>
                                                                    <td>{typeof row.points_from_rules !== 'undefined' ? row.points_from_rules : (typeof row.pointsFromRules !== 'undefined' ? row.pointsFromRules : 0)}</td>
                                                                    <td>{typeof row.points_from_customers !== 'undefined' ? row.points_from_customers : (typeof row.pointsFromCustomers !== 'undefined' ? row.pointsFromCustomers : 0)}</td>
                                                                    <td>{row.not_on_time_count}</td>
                                                                    <td>{row.reclamations_count}</td>
                                                                    <td style={{ minWidth: 200 }}>
                                                                        <Box display="flex" alignItems="center" gap={1}>
                                                                            <Box width="100%" mr={1}>
                                                                                <LinearProgress
                                                                                    variant="determinate"
                                                                                    value={row.progress_percentage}
                                                                                    sx={{
                                                                                        height: 10,
                                                                                        borderRadius: 5,
                                                                                        background: '#e0e0e0',
                                                                                        '& .MuiLinearProgress-bar': {
                                                                                            background: 'linear-gradient(90deg, #96D6BF 0%, #35B084 100%)'
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        </Box>
                                                                    </td>
                                                                    <td>
                                                                        <Typography style={{ fontWeight: 600 }}>{row.estimated_completion_date ?? '-'}</Typography>
                                                                    </td>
                                                                    <td>
                                                                        <Typography style={{ color: row.completed_target ? '#43b97f' : undefined, fontWeight: 600 }}>{row.reward}</Typography>
                                                                    </td>
                                                                    <td>
                                                                        <Typography style={{ color: row.completed_target ? '#43b97f' : undefined, fontWeight: 600 }}>{row.super_bonus}</Typography>
                                                                    </td>
                                                                    <td>
                                                                        <Typography style={{ color: row.completed_target ? '#43b97f' : undefined, fontWeight: 600 }}>{row.total_earned_bonuses}</Typography>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <table className='unclickable'>
                                                    <thead>
                                                        <tr>
                                                            <th>{trans('labels.user')}</th>
                                                            <th>{trans('labels.reservations')}</th>
                                                            <th>{trans('labels.delays')}</th>
                                                            <th>{trans('labels.reclamations')}</th>
                                                            <th>{trans('labels.progress')}</th>
                                                            <th>{trans('labels.completionForecast')}</th>
                                                            <th>{trans('labels.bonus')}</th>
                                                            <th>{trans('labels.super_bonus')}</th>
                                                            <th>{trans('labels.total_earned_bonuses')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {loading ? (
                                                            <tr>
                                                                <td colSpan="9" align="center">
                                                                    <Skeleton height={45} count={5} />
                                                                </td>
                                                            </tr>
                                                        ) : rows.length === 0 ? (
                                                            <tr>
                                                                <td colSpan="9" align="center">
                                                                    <NoDataFound />
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            rows.map((row, idx) => (
                                                                <tr
                                                                    key={row.employee_id || idx}
                                                                    style={row.completed_target ? { backgroundColor: '#e6f9ed' } : {}}
                                                                >
                                                                    <td>
                                                                        <Box display="flex" alignItems="center" gap={1}>
                                                                            <Avatar style={{ width: 28, height: 28, marginRight: 8 }} />
                                                                            <span>{row.employee_name}</span>
                                                                        </Box>
                                                                    </td>
                                                                    <td>{row.total_reservations}</td>
                                                                    <td>{row.not_on_time_count}</td>
                                                                    <td>{row.reclamations_count}</td>
                                                                    <td style={{ minWidth: 200 }}>
                                                                        <Box display="flex" alignItems="center" gap={1}>
                                                                            <Box width="100%" mr={1}>
                                                                                <LinearProgress
                                                                                    variant="determinate"
                                                                                    value={row.progress_percentage}
                                                                                    sx={{
                                                                                        height: 10,
                                                                                        borderRadius: 5,
                                                                                        background: '#e0e0e0',
                                                                                        '& .MuiLinearProgress-bar': {
                                                                                            background: 'linear-gradient(90deg, #96D6BF 0%, #35B084 100%)'
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        </Box>
                                                                    </td>
                                                                    <td>
                                                                        <Typography style={{ fontWeight: 600 }}>{row.estimated_completion_date ?? '-'}</Typography>
                                                                    </td>
                                                                    <td>
                                                                        <Typography style={{ color: row.completed_target ? '#43b97f' : undefined, fontWeight: 600 }}>{row.reward}</Typography>
                                                                    </td>
                                                                    <td>
                                                                        <Typography style={{ color: row.completed_target ? '#43b97f' : undefined, fontWeight: 600 }}>{row.super_bonus}</Typography>
                                                                    </td>
                                                                    <td>
                                                                        <Typography style={{ color: row.completed_target ? '#43b97f' : undefined, fontWeight: 600 }}>{row.total_earned_bonuses}</Typography>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            )}
                                        </td>
                                    }
                                </tr>
                            </Fragment>
                        ))}
                    </tbody >
                </table >
            )
            }
        </>
    )
}

export default BonusPoliciesDashboard
